import { useState } from "react";
import { ProjectCollection } from "../../components/ToolPage";
import type { MonitoringProject } from "../../api/monitoring";
export default function MonitoringProjectList({
  projects,
  loading,
  error,
  activeIds,
  activeUnknown,
  onDelete,
  onCheck,
  onPause,
  onResume,
}: {
  projects: MonitoringProject[];
  loading: boolean;
  error?: string | null;
  activeIds: string[];
  activeUnknown: boolean;
  onDelete: (p: MonitoringProject) => void;
  onCheck: (id: string) => Promise<void>;
  onPause: (id: string) => Promise<void>;
  onResume: (id: string) => Promise<void>;
}) {
  const [pending, setPending] = useState<string | null>(null);
  const run = async (id: string, action: (id: string) => Promise<void>) => {
    if (pending) return;
    setPending(id);
    try {
      await action(id);
    } finally {
      setPending(null);
    }
  };
  return (
    <ProjectCollection
      toolId="monitoring"
      loading={loading}
      error={error}
      statusOptions={[
        { value: "active", label: "Active" },
        { value: "running", label: "Running" },
        { value: "paused", label: "Paused" },
        { value: "error", label: "Error" },
      ]}
      items={projects.map((p) => {
        const running = activeIds.includes(p.id),
          busy = pending === p.id;
        return {
          id: p.id,
          title: p.name,
          description: p.url,
          href: `/monitoring/${encodeURIComponent(p.id)}`,
          updatedAt: p.lastCheck || p.createdAt,
          status: {
            label: running ? "Running" : p.status,
            tone:
              p.status === "error" ? ("danger" as const) : ("neutral" as const),
          },
          metadata: [
            { label: "Changes", value: p.changeCount ?? "—" },
            {
              label: "Last check",
              value: p.lastCheck
                ? new Date(p.lastCheck).toLocaleDateString()
                : "Never",
            },
          ],
          actions: [
            {
              label: "Check now",
              onSelect: () => {
                void run(p.id, onCheck);
              },
              pending: busy,
              disabledReason: activeUnknown
                ? "Current crawl status is unavailable"
                : running
                  ? "A check is already running"
                  : p.status === "paused"
                    ? "Resume monitoring before checking"
                    : undefined,
            },
            {
              label:
                p.status === "paused"
                  ? "Resume monitoring"
                  : "Pause monitoring",
              onSelect: () => {
                void run(p.id, p.status === "paused" ? onResume : onPause);
              },
              pending: busy,
            },
            {
              label: "Rename and settings",
              href: `/monitoring/${encodeURIComponent(p.id)}/settings`,
            },
            { label: "Delete project", onSelect: () => onDelete(p) },
          ],
        };
      })}
    />
  );
}
